import React, { useState } from 'react';
import { Linkedin, Mail, Twitter, Link2, Check, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { BlogHeroVisual } from './BlogDiagrams';

// Share buttons + a social preview card (how it looks on LinkedIn / email).
const BlogShare = ({ post }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Share the crawler-friendly URL so LinkedIn / WhatsApp / X render a rich card.
  const shareUrl = `${origin}/api/share/${post.slug}`;
  const encUrl = encodeURIComponent(shareUrl);
  const encTitle = encodeURIComponent(post.title);
  const encBody = encodeURIComponent(`${post.excerpt || post.title}\n\nRead more: ${shareUrl}`);

  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`;
  const twitter = `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`;
  const email = `mailto:?subject=${encTitle}&body=${encBody}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: 'Link copied', description: 'Shows a rich preview on LinkedIn, WhatsApp & email.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy', description: shareUrl });
    }
  };

  const Btn = ({ href, onClick, Icon, label, testId, className }) => {
    const inner = (
      <span className="inline-flex items-center justify-center gap-2 w-full">
        <Icon className="h-4 w-4" /> {label}
      </span>
    );
    const cls = `rounded-xl h-11 px-3 font-semibold text-sm btn-transition flex items-center justify-center ${className}`;
    return href ? (
      <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testId}>{inner}</a>
    ) : (
      <button type="button" onClick={onClick} className={cls} data-testid={testId}>{inner}</button>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="blog-share">
      <p className="text-xs uppercase tracking-[0.25em] text-[#FF4E50] font-bold mb-4 inline-flex items-center gap-2">
        <Share2 className="h-3.5 w-3.5" /> Share this article
      </p>

      {/* Social preview card */}
      <div className="rounded-xl border border-slate-200 overflow-hidden mb-4" data-testid="blog-share-preview">
        <BlogHeroVisual post={post} rounded="rounded-none" />
        <div className="p-4 bg-slate-50">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">proofnation.com</p>
          <p className="mt-1 text-sm font-black text-[#050F2E] leading-snug line-clamp-2">{post.title}</p>
          <p className="mt-1 text-xs text-slate-500 leading-snug line-clamp-2">{post.excerpt}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Btn href={linkedin} Icon={Linkedin} label="LinkedIn" testId="share-linkedin" className="bg-[#0A66C2] text-white hover:opacity-90" />
        <Btn href={email} Icon={Mail} label="Email" testId="share-email" className="bg-[#0A2A63] text-white hover:bg-[#061a3f]" />
        <Btn href={twitter} Icon={Twitter} label="X" testId="share-twitter" className="bg-white text-[#0A2A63] border border-slate-200 hover:border-[#0A2A63]" />
        <Btn onClick={copy} Icon={copied ? Check : Link2} label={copied ? 'Copied' : 'Copy link'} testId="share-copy" className="bg-white text-[#0A2A63] border border-slate-200 hover:border-[#0A2A63]" />
      </div>
    </div>
  );
};

export default BlogShare;
